import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useToastStore, type Toast as ToastT } from '@/store/toasts';
import styles from './ToastContainer.module.css';

/**
 * Globaler Toast-Stack. Wird einmal pro App in TripApp / AdminApp gemountet
 * und rendert alle aktuellen Toasts aus dem Zustand-Store via Portal an
 * document.body. Auto-Dismiss läuft pro Toast als eigenes setTimeout —
 * eingestellt im Container, weil Side-Effects nichts im Store verloren haben.
 */
export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);

  if (toasts.length === 0) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className={styles.stack} role="region" aria-label="Benachrichtigungen">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>,
    document.body,
  );
}

function ToastItem({ toast }: { toast: ToastT }) {
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    if (toast.timeoutMs === null) return;
    const id = window.setTimeout(() => dismiss(toast.id), toast.timeoutMs);
    return () => window.clearTimeout(id);
  }, [toast.id, toast.timeoutMs, dismiss]);

  return (
    <div
      className={`${styles.toast} ${styles[toast.kind]}`}
      role={toast.kind === 'error' ? 'alert' : 'status'}
      aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
    >
      <span className={styles.icon} aria-hidden="true">
        {toast.kind === 'success' ? '✅' : toast.kind === 'error' ? '⚠️' : 'ℹ️'}
      </span>
      <span className={styles.message}>{toast.message}</span>
      <button
        type="button"
        className={styles.close}
        onClick={() => dismiss(toast.id)}
        aria-label="Benachrichtigung schließen"
      >
        ×
      </button>
    </div>
  );
}
